import React, { useEffect, useRef, useState } from 'react';
import { useSupport } from '../context/SupportContext';

/* Хук ширины экрана — идентичен тому, что в ChatBot.js */
function useIsMobile(breakpoint = 640) {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [breakpoint]);
    return isMobile;
}

const SupportChat = () => {
    const {
        messages, unread, connected, sending,
        isOpen, openChat, closeChat, sendMessage,
    } = useSupport();
    const [input, setInput] = useState('');
    const bottomRef = useRef(null);
    const isMobile = useIsMobile(640);

    useEffect(() => {
        if (isOpen) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isOpen]);

    const handleSend = async () => {
        const t = input.trim();
        if (!t) return;
        setInput('');
        await sendMessage(t);
    };

    // Одинаковые с ChatBot размеры и позиция — чтобы кнопки были выровнены
    const btnSize = isMobile ? 52 : 60;
    const btnBottom = isMobile ? 16 : 24;
    const btnLeft = isMobile ? 16 : 24;

    return (
        <>
            {/* Кнопка-плавашка слева внизу */}
            {!isOpen && (
                <button
                    onClick={openChat}
                    aria-label="Чат с оператором"
                    style={{
                        position: 'fixed',
                        bottom: btnBottom,
                        left: btnLeft,
                        background: '#1C1917',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: btnSize,
                        height: btnSize,
                        fontSize: isMobile ? 20 : 24,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                        cursor: 'pointer',
                        zIndex: 998,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                    🎧
                    {unread > 0 && (
                        <span style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            background: '#e53935',
                            color: '#fff',
                            borderRadius: 100,
                            minWidth: 18,
                            height: 18,
                            fontSize: 11,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 5px',
                        }}>
                            {unread > 9 ? '9+' : unread}
                        </span>
                    )}
                </button>
            )}

            {isOpen && (
                <div style={{
                    position: 'fixed',
                    bottom: isMobile ? 76 : 100,
                    left: isMobile ? 12 : 24,
                    right: isMobile ? 12 : 'auto',
                    width: isMobile ? 'auto' : 360,
                    maxWidth: 'calc(100vw - 24px)',
                    maxHeight: 'calc(100vh - 100px)',
                    background: '#fff',
                    borderRadius: 20,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                    border: '1px solid #E8E3DD',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 1000,
                    overflow: 'hidden',
                }}>
                    <div style={{
                        padding: '12px 16px',
                        background: '#1C1917',
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <div>
                            <div style={{ fontWeight: 600 }}>Поддержка</div>
                            <div style={{ fontSize: 11, opacity: 0.75 }}>
                                {connected ? '🟢 Онлайн' : '🟡 Переподключение…'}
                            </div>
                        </div>
                        <button onClick={closeChat} aria-label="Закрыть"
                            style={{
                                background: 'none', border: 'none', color: '#fff',
                                fontSize: 20, cursor: 'pointer', padding: 0, lineHeight: 1,
                            }}>
                            ✕
                        </button>
                    </div>

                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        background: '#FBF9F6',
                        minHeight: 220,
                        maxHeight: isMobile ? 'calc(100vh - 260px)' : 380,
                    }}>
                        {messages.length === 0 && (
                            <div style={{
                                textAlign: 'center', color: '#999', fontSize: 13,
                                padding: '40px 12px', lineHeight: 1.5,
                            }}>
                                👋 Напишите вопрос — оператор ответит здесь же.
                            </div>
                        )}
                        {messages.map((m) => {
                            if (m.role === 'system') {
                                return (
                                    <div key={m.id} style={{
                                        textAlign: 'center', fontSize: 12,
                                        color: '#999', padding: 4,
                                    }}>
                                        {m.text}
                                    </div>
                                );
                            }
                            const isClient = m.role === 'client';
                            return (
                                <div key={m.id} style={{
                                    alignSelf: isClient ? 'flex-end' : 'flex-start',
                                    maxWidth: '85%',
                                    padding: '9px 13px',
                                    borderRadius: isClient
                                        ? '16px 16px 4px 16px'
                                        : '16px 16px 16px 4px',
                                    background: isClient ? '#6B7A6A' : '#fff',
                                    color: isClient ? '#fff' : '#1C1917',
                                    fontSize: 14,
                                    lineHeight: 1.4,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    border: isClient ? 'none' : '1px solid #E8E3DD',
                                    opacity: m.temp_id ? 0.65 : 1,
                                }}>
                                    {m.text}
                                </div>
                            );
                        })}
                        <div ref={bottomRef} />
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: 8,
                        padding: 12,
                        borderTop: '1px solid #E8E3DD',
                        background: '#fff',
                    }}>
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            placeholder="Ваш вопрос..."
                            disabled={sending}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                padding: '8px 14px',
                                border: '1px solid #E8E3DD',
                                borderRadius: 100,
                                fontSize: 14,
                                outline: 'none',
                            }}
                        />
                        <button
                            onClick={handleSend}
                            disabled={sending || !input.trim()}
                            aria-label="Отправить"
                            style={{
                                background: '#6B7A6A',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                flexShrink: 0,
                                cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
                                fontSize: 16,
                                opacity: sending || !input.trim() ? 0.5 : 1,
                            }}
                        >➤</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default SupportChat;